/**
 * shared/pmove/context.ts — THE one place a PmoveContext is built. Server (sim/core), the
 * single-player worker and client prediction all call createPmoveContext so the wiring
 * (weapon hooks, defs, dvars, event sink) can never differ between them [NET §12 pitfall 1].
 *
 * OWNER: integration.
 */
import * as C from '../constants';
import type { CollisionWorld, ClipEntity } from '../collision/types';
import { makeTraceFn } from '../collision/boxworld';
import { addPredictableEvent } from '../playerstate';
import { getWeaponDef } from '../weapons/defs';
import { pmWeaponHooks } from '../weapons/pm_weapon';
import type { PmoveContext, PmoveDvars } from './types';

export const DEFAULT_PMOVE_DVARS: Readonly<PmoveDvars> = Object.freeze({
  stopspeed: C.PM_STOPSPEED,
  friction: C.PM_FRICTION,
  backSpeedScale: C.PM_BACK_SPEED_SCALE,
  strafeSpeedScale: C.PM_STRAFE_SPEED_SCALE,
  sprintForwardMinimum: C.PM_SPRINT_FORWARD_MIN,
  standingViewHeight: C.VIEWHEIGHT_STAND,
  jumpHeight: C.PM_JUMP_HEIGHT,
  jumpStepSize: C.PM_JUMP_STEP_SIZE,
  jumpSlowdownEnable: C.PM_JUMP_SLOWDOWN_ENABLE,
  jumpLadderPushVel: C.PM_JUMP_LADDER_PUSH_VEL,
  jumpSpreadAdd: C.PM_JUMP_SPREAD_ADD,
  dmgTimerMinScale: 0, // ESTIMATE (PM §6: 0 => no damage slowdown)
  dmgTimerMaxTime: 750,
  minWalkNormal: C.PM_MIN_WALK_NORMAL,
  fallDamageMinHeight: C.FALL_DAMAGE_MIN_HEIGHT,
  fallDamageMaxHeight: C.FALL_DAMAGE_MAX_HEIGHT,
  pitchUpLimit: C.PITCH_UP_LIMIT,
  pitchDownLimit: C.PITCH_DOWN_LIMIT,
});

export interface PmoveContextOptions {
  world: CollisionWorld;
  /** Other players' clip boxes at the time this pmove runs (server: current; client: interpolated). */
  getEntities?: () => readonly ClipEntity[];
  dvars?: Partial<PmoveDvars>;
}

const NO_ENTS: readonly ClipEntity[] = [];

export function createPmoveContext(opts: PmoveContextOptions): PmoveContext {
  const getEntities = opts.getEntities ?? (() => NO_ENTS);
  const trace = makeTraceFn(opts.world, getEntities);
  return {
    trace,
    pointContents: (p, _pass) => {
      let c = opts.world.pointContents(p, 0xffffffff);
      for (const e of getEntities()) {
        if (p[0] >= e.absMins[0] && p[0] <= e.absMaxs[0] && p[1] >= e.absMins[1] && p[1] <= e.absMaxs[1] && p[2] >= e.absMins[2] && p[2] <= e.absMaxs[2]) c |= e.contents;
      }
      return c;
    },
    weaponDef: getWeaponDef,
    weapons: pmWeaponHooks,
    dvars: { ...DEFAULT_PMOVE_DVARS, ...opts.dvars },
    addEvent: addPredictableEvent,
    touchEnts: [],
  };
}
